import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, KeyRound, Check } from "lucide-react";
import api, { apiError } from "../lib/api";

const ROLE_LOGIN = { school: "/school/auth", parent: "/parent/auth", superadmin: "/super/login" };
const ROLE_LABEL = { school: "school admin", parent: "parent / student", superadmin: "super admin" };

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const role = params.get("role") || "school";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDE68A]/30" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="reset-brand">
          <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
            <Clock className="w-5 h-5" />
          </div>
          <span className="font-display font-black text-2xl">MyTimetable.co.uk</span>
        </Link>

        <div className="nb-card p-8 bg-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-xl border-2 border-zinc-900 bg-[#FDE68A] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <KeyRound className="w-7 h-7" />
            </div>
            <h1 className="font-display font-black text-2xl mt-4">Set a new password</h1>
            <p className="text-sm text-zinc-500 mt-1">For your {ROLE_LABEL[role] || "school admin"} account.</p>
          </div>

          {!token && (
            <p className="text-sm font-bold text-red-600 text-center" data-testid="reset-error">This reset link is missing its token — request a fresh one from the login page.</p>
          )}

          {token && !done && (
            <form onSubmit={submit} className="space-y-4">
              {error && <p className="text-sm font-bold text-red-600 bg-red-50 border-2 border-red-300 rounded-lg p-3" data-testid="reset-error">{error}</p>}
              <div>
                <label className="nb-label" htmlFor="reset-new">New password</label>
                <input id="reset-new" type="password" className="nb-input mt-2" data-testid="reset-new-password-input"
                  placeholder="At least 8 characters" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              </div>
              <div>
                <label className="nb-label" htmlFor="reset-confirm">Confirm new password</label>
                <input id="reset-confirm" type="password" className="nb-input mt-2" data-testid="reset-confirm-input"
                  value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={8} />
              </div>
              <button type="submit" disabled={busy} data-testid="reset-submit" className="nb-btn w-full bg-[#FDE68A] py-3">
                {busy ? "Saving…" : "Set new password"}
              </button>
            </form>
          )}

          {done && (
            <div className="text-center" data-testid="reset-success">
              <div className="w-12 h-12 mx-auto rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <p className="font-bold mt-4">Password updated — you're all set.</p>
              <button onClick={() => navigate(ROLE_LOGIN[role] || "/school/auth")} data-testid="reset-login-link"
                className="nb-btn w-full bg-[#1E3A8A] text-white py-3 mt-4">
                Continue to log in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
